import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calculator, Package, Scale, Ruler, Info, ArrowRight, RefreshCw, Phone, Mail } from 'lucide-react';

const VolumetricWeightCalculatorPage = () => {
  const [dimensionUnit, setDimensionUnit] = useState('in'); // 'in' or 'cm'
  const [weightUnit, setWeightUnit] = useState('kg'); // 'kg' or 'lb'
  const [dimensions, setDimensions] = useState({
    length: '',
    width: '',
    height: '',
    actualWeight: ''
  });
  const [results, setResults] = useState(null);

  // Box images from assets - CORRECTLY MAPPED
  const boxImages = {
    medium: "https://customer-assets-39nsmqrw.emergentagent.net/job_angry-ellis-7/artifacts/8b4641322d6ec267_B6B01E10-A9CC-48BE-8FD3-BCC8C1D24013.png", // Home Depot Medium - 20 KG
    large: "https://customer-assets-39nsmqrw.emergentagent.net/job_angry-ellis-7/artifacts/ce6c23e56e2001f7_D7FC51AD-8178-445E-8468-606B13013131.png", // Home Depot Large - 23 KG
    extraLarge: "https://customer-assets-39nsmqrw.emergentagent.net/job_angry-ellis-7/artifacts/9c2981ca08bdc468_0F8B4296-EB01-443E-8B49-AAE684B63FCA.png" // Canadian Tire Extra-Large - 32 KG
  };

  // Convert units when switching
  const convertDimension = (value, fromUnit, toUnit) => {
    if (!value) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    
    if (fromUnit === 'in' && toUnit === 'cm') {
      return (num * 2.54).toFixed(2);
    } else if (fromUnit === 'cm' && toUnit === 'in') {
      return (num / 2.54).toFixed(2);
    }
    return value;
  };

  const convertWeight = (value, fromUnit, toUnit) => {
    if (!value) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    
    if (fromUnit === 'kg' && toUnit === 'lb') {
      return (num * 2.20462).toFixed(2);
    } else if (fromUnit === 'lb' && toUnit === 'kg') {
      return (num / 2.20462).toFixed(2);
    }
    return value;
  };

  const handleDimensionUnitChange = (newUnit) => {
    setDimensions(prev => ({
      length: convertDimension(prev.length, dimensionUnit, newUnit),
      width: convertDimension(prev.width, dimensionUnit, newUnit),
      height: convertDimension(prev.height, dimensionUnit, newUnit),
      actualWeight: prev.actualWeight
    }));
    setDimensionUnit(newUnit);
  };

  const handleWeightUnitChange = (newUnit) => {
    setDimensions(prev => ({
      ...prev,
      actualWeight: convertWeight(prev.actualWeight, weightUnit, newUnit)
    }));
    setWeightUnit(newUnit);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Prevent negative values
    if (parseFloat(value) < 0) return;
    
    setDimensions(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    calculateWeight();
  }, [dimensions, dimensionUnit, weightUnit]);

  const calculateWeight = () => {
    const { length, width, height, actualWeight } = dimensions;
    
    if (!length || !width || !height || !actualWeight) {
      setResults(null);
      return;
    }

    let l = parseFloat(length);
    let w = parseFloat(width);
    let h = parseFloat(height);
    let actual = parseFloat(actualWeight);

    if (isNaN(l) || isNaN(w) || isNaN(h) || isNaN(actual)) {
      setResults(null);
      return;
    }

    // Convert to cm if in inches
    if (dimensionUnit === 'in') {
      l = l * 2.54;
      w = w * 2.54;
      h = h * 2.54;
    }

    // Convert actual weight to kg if in lbs
    let actualKg = weightUnit === 'lb' ? actual / 2.20462 : actual;

    // Calculate volumetric weight in kg using divisor 5000
    const volumetricKg = (l * w * h) / 5000;
    const chargeableKg = Math.max(actualKg, volumetricKg);

    // Convert to lbs for display
    const actualLb = actualKg * 2.20462;
    const volumetricLb = volumetricKg * 2.20462;
    const chargeableLb = chargeableKg * 2.20462;

    setResults({
      actualKg: actualKg.toFixed(2),
      actualLb: actualLb.toFixed(2),
      volumetricKg: volumetricKg.toFixed(2),
      volumetricLb: volumetricLb.toFixed(2),
      chargeableKg: chargeableKg.toFixed(2),
      chargeableLb: chargeableLb.toFixed(2)
    });
  };

  const resetCalculator = () => {
    setDimensions({
      length: '',
      width: '',
      height: '',
      actualWeight: ''
    });
    setResults(null);
  };

  const shippingBoxes = [
    {
      name: "Home Depot Medium Moving Box",
      dimensions: "23 × 15 × 15",
      dimensionsIn: "23 × 15 × 15 inches",
      category: "20 KG",
      volumetricKg: "16.96",
      recommendedWeight: "20 KG",
      description: "Suitable for approximately 20 kg of household belongings, clothing, personal items, and other permitted shipping contents.",
      image: boxImages.medium
    },
    {
      name: "Home Depot Large Moving Box",
      dimensions: "28 × 14 × 16",
      dimensionsIn: "28 × 14 × 16 inches",
      category: "23 KG",
      volumetricKg: "20.56",
      recommendedWeight: "23 KG",
      description: "Suitable for approximately 23 kg of household belongings, clothing, and relocation items.",
      image: boxImages.large
    },
    {
      name: "Canadian Tire Extra Large Moving Box",
      dimensions: "24 × 20 × 20",
      dimensionsIn: "24 × 20 × 20 inches",
      category: "32 KG",
      volumetricKg: "31.46",
      recommendedWeight: "32 KG",
      description: "Suitable for larger household belongings, clothing, and relocation shipments.",
      image: boxImages.extraLarge
    }
  ];

  return (
    <>
      <Helmet>
        <title>Volumetric Weight Calculator | Shippzy Canada</title>
        <meta
          name="description"
          content="Calculate volumetric weight in inches or centimetres, compare actual and chargeable weight, and explore shipping box recommendations with Shippzy."
        />
        <meta name="keywords" content="volumetric weight calculator, dimensional weight calculator, chargeable weight, shipping calculator, box recommendations" />
        <link rel="canonical" href="https://www.shippzy.ca/actual-weight-vs-volumetric-weight" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-red-600 via-red-700 to-orange-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Calculator className="mx-auto mb-6" size={48} />
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Volumetric Weight Calculator
              </h1>
              <p className="text-lg md:text-xl text-red-100">
                Calculate your shipment's chargeable weight instantly.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Calculator Section */}
        <section className="py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl p-6 md:p-10"
            >
              {/* Unit Selectors */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Dimension Unit */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    <Ruler className="inline mr-2" size={18} />
                    Dimension Unit
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleDimensionUnitChange('in')}
                      className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                        dimensionUnit === 'in'
                          ? 'bg-red-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Inches (in)
                    </button>
                    <button
                      onClick={() => handleDimensionUnitChange('cm')}
                      className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                        dimensionUnit === 'cm'
                          ? 'bg-red-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Centimetres (cm)
                    </button>
                  </div>
                </div>

                {/* Weight Unit */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    <Scale className="inline mr-2" size={18} />
                    Weight Unit
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleWeightUnitChange('kg')}
                      className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                        weightUnit === 'kg'
                          ? 'bg-red-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Kilograms (kg)
                    </button>
                    <button
                      onClick={() => handleWeightUnitChange('lb')}
                      className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                        weightUnit === 'lb'
                          ? 'bg-red-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Pounds (lbs)
                    </button>
                  </div>
                </div>
              </div>

              {/* Input Fields */}
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Length ({dimensionUnit})
                  </label>
                  <input
                    type="number"
                    name="length"
                    value={dimensions.length}
                    onChange={handleInputChange}
                    min="0"
                    step="0.1"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-600 focus:outline-none transition-colors"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Width ({dimensionUnit})
                  </label>
                  <input
                    type="number"
                    name="width"
                    value={dimensions.width}
                    onChange={handleInputChange}
                    min="0"
                    step="0.1"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-600 focus:outline-none transition-colors"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Height ({dimensionUnit})
                  </label>
                  <input
                    type="number"
                    name="height"
                    value={dimensions.height}
                    onChange={handleInputChange}
                    min="0"
                    step="0.1"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-600 focus:outline-none transition-colors"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Actual Weight ({weightUnit})
                  </label>
                  <input
                    type="number"
                    name="actualWeight"
                    value={dimensions.actualWeight}
                    onChange={handleInputChange}
                    min="0"
                    step="0.1"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-600 focus:outline-none transition-colors"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Reset Button */}
              <div className="flex justify-end mb-6">
                <button
                  onClick={resetCalculator}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  <RefreshCw size={18} />
                  Reset
                </button>
              </div>

              {/* Results */}
              {results && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border-2 border-red-200"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                    Calculation Results
                  </h3>

                  <div className="grid md:grid-cols-3 gap-6 mb-6">
                    {/* Actual Weight */}
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                      <p className="text-sm text-gray-600 mb-2">Actual Weight</p>
                      <p className="text-2xl font-bold text-gray-900">{results.actualKg} kg</p>
                      <p className="text-sm text-gray-500">({results.actualLb} lbs)</p>
                    </div>

                    {/* Volumetric Weight */}
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                      <p className="text-sm text-gray-600 mb-2">Volumetric Weight</p>
                      <p className="text-2xl font-bold text-gray-900">{results.volumetricKg} kg</p>
                      <p className="text-sm text-gray-500">({results.volumetricLb} lbs)</p>
                    </div>

                    {/* Chargeable Weight */}
                    <div className="bg-gradient-to-br from-red-600 to-orange-600 rounded-lg p-4 text-center shadow-lg">
                      <p className="text-sm text-red-100 mb-2 font-semibold">Chargeable Weight</p>
                      <p className="text-3xl font-bold text-white">{results.chargeableKg} kg</p>
                      <p className="text-sm text-red-100">({results.chargeableLb} lbs)</p>
                    </div>
                  </div>

                  {/* Explanation */}
                  <div className="bg-white rounded-lg p-4 border-l-4 border-red-600">
                    <p className="text-sm text-gray-700">
                      <Info className="inline mr-2 text-red-600" size={18} />
                      <strong>Courier charges are generally calculated using whichever is greater: actual weight or volumetric weight.</strong>
                    </p>
                  </div>

                  <p className="text-xs text-gray-500 mt-4 text-center">
                    * Volumetric divisors and weight rounding policies may vary depending on the carrier and shipping service.
                  </p>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>

        {/* Shipping Box Recommendations */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Choose the Right Box for Your Shipment
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Smart packing starts with choosing the right box. Explore our recommended shipping boxes to help reduce unnecessary volumetric weight.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {shippingBoxes.map((box, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow border-2 border-gray-100"
                >
                  {/* Box Image */}
                  <div className="bg-gray-50 p-6 flex items-center justify-center h-64">
                    <img
                      src={box.image}
                      alt={box.name}
                      className="max-h-full max-w-full object-contain"
                      loading="lazy"
                      decoding="async"
                      width="300"
                      height="256"
                    />
                  </div>

                  {/* Box Details */}
                  <div className="p-6">
                    {/* Category Badge */}
                    <div className="inline-block bg-gradient-to-r from-red-600 to-orange-600 text-white px-4 py-2 rounded-full font-bold text-lg mb-4">
                      {box.category} PACKING CATEGORY
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {box.name}
                    </h3>

                    <div className="space-y-2 mb-4">
                      <p className="text-gray-700">
                        <strong>Dimensions:</strong> {box.dimensionsIn}
                      </p>
                      <p className="text-gray-700">
                        <strong>Calculated Volumetric Weight:</strong> ~{box.volumetricKg} kg
                      </p>
                      <p className="text-gray-700">
                        <strong>Recommended Contents Weight:</strong> {box.recommendedWeight}
                      </p>
                    </div>

                    <p className="text-sm text-gray-600">
                      {box.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why It Matters Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                Why Does Volumetric Weight Matter?
              </h2>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <p className="text-lg text-gray-700 mb-4">
                  Large boxes occupy more space during transportation. Even when a package is lightweight, its dimensions can increase its chargeable shipping weight.
                </p>
                <p className="text-lg text-gray-700">
                  Choosing appropriately sized boxes can help customers avoid unnecessary shipping costs. By selecting boxes that match your actual shipping needs, you can minimize volumetric weight charges while ensuring your items are safely packed.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Free Shipping Boxes Offer */}
        <section className="py-16 bg-gradient-to-br from-red-600 via-red-700 to-orange-600 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <Package size={64} className="mx-auto mb-6" />
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                FREE SHIPPING BOXES AVAILABLE
              </h2>
              <p className="text-2xl font-semibold text-red-100 mb-8">
                Pack Smart. Ship with Shippzy.
              </p>

              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 mb-6">
                <p className="text-xl mb-6">
                  Shipping boxes with exact volumetric weight categories of <strong className="text-2xl">20 KG, 23 KG, and 30 KG</strong> are available <strong>FREE in-store</strong> with any shipping booking.
                </p>

                <div className="flex flex-wrap justify-center gap-4 text-2xl md:text-3xl font-bold">
                  <span className="bg-white text-red-600 px-6 py-3 rounded-lg shadow-lg">
                    20 KG
                  </span>
                  <span className="bg-white text-red-600 px-6 py-3 rounded-lg shadow-lg">
                    23 KG
                  </span>
                  <span className="bg-white text-red-600 px-6 py-3 rounded-lg shadow-lg">
                    30 KG
                  </span>
                </div>

                <p className="text-lg mt-6 font-semibold">
                  FREE WITH ANY SHIPPING
                </p>
              </div>

              <p className="text-sm text-red-100">
                * Free boxes are available in-store with any shipping booking, subject to availability.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Ready to Ship with Shippzy?
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                From domestic parcels to international couriers, suitcase shipping, and complete relocation services, Shippzy makes shipping simpler.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg"
                >
                  Get a Shipping Quote
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/schedule-pickup"
                  className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg"
                >
                  Schedule a Pickup
                  <ArrowRight size={20} />
                </Link>
              </div>

              {/* Contact Info */}
              <div className="bg-gray-50 rounded-xl p-6 inline-block">
                <h3 className="text-xl font-bold text-gray-900 mb-4">SHIPPZY INC.</h3>
                <div className="space-y-2 text-gray-700">
                  <p className="flex items-center justify-center gap-2">
                    <Mail size={18} className="text-red-600" />
                    <a href="https://www.shippzy.ca" className="hover:text-red-600 transition-colors">
                      www.shippzy.ca
                    </a>
                  </p>
                  <p className="flex items-center justify-center gap-2">
                    <Phone size={18} className="text-red-600" />
                    <a href="tel:437-616-0758" className="hover:text-red-600 transition-colors">
                      437-616-0758
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default VolumetricWeightCalculatorPage;
